import React, { useCallback, useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { observer } from "mobx-react-lite";

import AddNewButton from "../../../../components/buttons/add-new-button/AddNewButton";
import WarningBeforeAction from "../../../../components/data-components/WarningBeforeAction";
import RadioButton from "../../../../components/radio-button/RadioButton";
import { secondsToMinutesAndSeconds } from "../../../../helpers/MathHelpers";
import { CurrentInspectionStorage } from "../../../../services/inspection/CurrentInspectionStorage";
import { VideoAnalysisService } from "../../../../services/inspection/VideoAnalysisService";
import { translationService } from "../../../../services/translation/TranslationService";
import { globalModule } from "../../../../services/service-provider/GlobalModule";
import {
  emptyVADetection,
  VADetection,
  videoPlayerService,
} from "../../../../services/video-player/VideoPlayerService";
import "./composition.scss";
import DisplayImage from "../../../../components/data-components/DisplayImage";
import LoadingSpinner from "../../../../components/loading-spinner/LoadingSpinner";
import Svg from "../../../../components/svg/Svg";
import { Warning } from "../../../../components/svg/svgImageList";
import { Timestamp } from "../../../../services/video-player/TimestampsData";
import { permissionsService } from "../../../../services/authorization/UserPermissionsService";
import {
  CreateDamage,
  CreateDamageInAssignedProject,
} from "../../../../services/authorization/Permissions";
import useModal from "../../../../components/dialog/useModal";
import { SecondaryCodeEnum } from "../../../../generated/Damages";

interface AddNewDamageButtonProps {
  disabled: boolean;
  videoAnalysisService?: VideoAnalysisService;
  currentInspectionStorage: CurrentInspectionStorage;
}

export const AddNewDamageButton = observer((props: AddNewDamageButtonProps) => {
  const { disabled, videoAnalysisService, currentInspectionStorage } = props;
  const { toggleModal, isVisible } = useModal();
  const history = useHistory();

  const [selectedDamageDetection, setSelectedDamageDetection] = useState<VADetection>(
    videoPlayerService.VADamageDetections?.[0] ?? emptyVADetection
  );
  const [detectionTimestamp, setDetectionTimestamp] = useState(0);

  const noDamagesDetected = useCallback(
    () => videoPlayerService.vaDetectionsWithoutEmptyDetection.length === 0,
    []
  );
  const oneDamageDetected = useCallback(
    () => videoPlayerService.vaDetectionsWithoutEmptyDetection.length === 1,
    []
  );
  const multipleDamagesDetected = useCallback(
    () => videoPlayerService.vaDetectionsWithoutEmptyDetection.length > 1,
    []
  );

  const handleAddNewDamageClick = useCallback(async () => {
    videoPlayerService.setSelectedVADetection(emptyVADetection);
    if (!currentInspectionStorage.currentInspection.hasVideo) {
      videoPlayerService.resetVideoPlayer();
      history.push(`${history.location.pathname}/create-damage`);
      return;
    }

    if (!videoAnalysisService) {
      history.push(`${history.location.pathname}/create-damage`);
      return;
    }

    videoPlayerService.takeScreenshotAtCurrentVideoPosition(
      currentInspectionStorage.currentInspection.video?.id!
    );

    setDetectionTimestamp(videoPlayerService.videoProgress);

    if (!videoAnalysisService?.analysisIsAvailable) {
      let detectionWithTimestamp = { ...emptyVADetection };
      detectionWithTimestamp.timestamp = videoPlayerService.videoProgress;
      videoPlayerService.setSelectedVADetection(detectionWithTimestamp);
    } else {
      const detections = videoAnalysisService.characterizationAtTime(
        videoPlayerService.videoProgress
      );
      videoPlayerService.computeVADamageDetections(detections);
      if (oneDamageDetected() || multipleDamagesDetected()) {
        setSelectedDamageDetection(videoPlayerService.VADamageDetections[0]);
      }
    }

    toggleModal();
  }, [
    currentInspectionStorage.currentInspection.hasVideo,
    currentInspectionStorage.currentInspection.video?.id,
    history,
    oneDamageDetected,
    multipleDamagesDetected,
    toggleModal,
    videoAnalysisService,
  ]);

  const handleConfirmationAction = useCallback(() => {
    const detectionWithTimestamp = {
      ...(videoPlayerService.VADamageDetections.find(
        (x) => x.mainCode === selectedDamageDetection?.mainCode
      ) ?? emptyVADetection),
    };

    detectionWithTimestamp.timestamp = detectionTimestamp;
    videoPlayerService.setSelectedVADetection(detectionWithTimestamp);

    history.push(`${history.location.pathname}/create-damage`);
  }, [detectionTimestamp, history, selectedDamageDetection]);

  const toggleAddNewDamage = useCallback(
    (timestamp: number) => {
      if (
        !permissionsService.userHasOneOfThePermissions([
          CreateDamage,
          CreateDamageInAssignedProject,
        ]) ||
        currentInspectionStorage.inspectionIsLockedBySomeoneElse
      )
        return;
      videoPlayerService.setVideoProgress(timestamp);
      handleAddNewDamageClick();
    },
    [currentInspectionStorage.inspectionIsLockedBySomeoneElse, handleAddNewDamageClick]
  );

  useEffect(() => {
    globalModule.videoService = {
      ...globalModule.videoService,
      addDamage: toggleAddNewDamage,
    };
  }, [toggleAddNewDamage]);

  return (
    <>
      <AddNewButton disabled={disabled} onClick={handleAddNewDamageClick} />
      <WarningBeforeAction
        isVisible={isVisible}
        titleKey={"CreateDamage.modal-confirmation.title"}
        buttonOk={{
          id: "run-va-ok",
          textKey: "CreateDamage.modal-confirmation.ok",
          action: handleConfirmationAction,
          disabled: videoPlayerService.lastScreenshotUri === "",
        }}
        onHide={videoPlayerService.resetLastScreenshot}
        cancelbuttonAction={videoPlayerService.resetLastScreenshot}
        toggleModal={toggleModal}
      >
        <div className="damage-modal-confirmation-sub-title">
          <div>{translationService.translate("CreateDamage.modal-confirmation.sub-title")}</div>
          {videoAnalysisService?.analysisIsAvailable && (
            <div>
              {noDamagesDetected() &&
                translationService.translate("CreateDamage.modal-confirmation.no-detection")}
              {oneDamageDetected() &&
                translationService.translate("CreateDamage.modal-confirmation.one-detection")}
              {multipleDamagesDetected() &&
                translationService.translate("CreateDamage.modal-confirmation.multiple-detection")}
            </div>
          )}
        </div>
        <AvailableDamageInformation
          imageTime={videoPlayerService.lastScreenshotTimestamp}
          imageUri={videoPlayerService.lastScreenshotUri}
          vaDetections={videoPlayerService.VADamageDetections}
          existingTimestamps={videoPlayerService.timestamps?.items ?? []}
          setDetectionCallback={setSelectedDamageDetection}
          selectedDetection={selectedDamageDetection}
        />
      </WarningBeforeAction>
    </>
  );
});

interface AvailableDamageInformationProps {
  imageTime: number;
  imageUri: string;
  vaDetections: VADetection[];
  existingTimestamps: Timestamp[];
  setDetectionCallback: React.Dispatch<React.SetStateAction<VADetection>>;
  selectedDetection: VADetection;
}

const AvailableDamageInformation = observer(
  ({
    imageTime,
    imageUri,
    vaDetections,
    existingTimestamps,
    setDetectionCallback,
    selectedDetection,
  }: AvailableDamageInformationProps) => {
    const screenshotTime: string = useMemo(
      () => secondsToMinutesAndSeconds(imageTime),
      [imageTime]
    );
    const selectedDetectionAlreadyAdded: boolean =
      !!selectedDetection &&
      useMemo(
        () =>
          !!existingTimestamps.find(
            (t) =>
              t.timestamp === imageTime &&
              t.mainCode === selectedDetection.mainCode &&
              t.c1Code === selectedDetection.c1Code &&
              t.c2Code === selectedDetection.c2Code
          ),
        [existingTimestamps, imageTime, selectedDetection]
      );

    const damagesDetected = useCallback(
      () => vaDetections?.filter((d) => d.mainCode !== emptyVADetection.mainCode).length !== 0,
      [vaDetections]
    );

    const isEmptyDamageSelected = useCallback(() => {
      return selectedDetection.mainCode === emptyVADetection.mainCode;
    }, [selectedDetection.mainCode]);

    const detectedCodeText = (damage: VADetection) => {
      const mainCode = damage.mainCode;

      return !damage.c1Code && !damage.c2Code
        ? mainCode
        : damage.c2Code
        ? `${mainCode} - ${damage.c1Code} / ${damage.c2Code}`
        : `${mainCode} - ${damage.c1Code}`;
    };

    return (
      <div className="damage-modal-confirmation-information">
        {imageUri ? (
          <div className="video-information">
            <div>
              {damagesDetected() && (
                <div className="detection-options">
                  {vaDetections.map((damage) => {
                    return (
                      <div key={damage.mainCode}>
                        <RadioButton
                          checked={selectedDetection.mainCode === damage.mainCode}
                          value={damage.mainCode}
                          onChange={() => {
                            setDetectionCallback(damage);
                          }}
                        />
                        <div className="radio-button-text">
                          {damage.mainCode === emptyVADetection.mainCode
                            ? translationService.translate(
                                "CreateDamage.modal-confirmation.add-custom-damage"
                              )
                            : detectedCodeText(damage)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {damagesDetected() && !isEmptyDamageSelected() && (
                <ul className="detection-list">
                  <li>{`MC: ${selectedDetection.mainCode}`}</li>
                  {selectedDetection.c1Code && (
                    <li>{`${SecondaryCodeEnum[SecondaryCodeEnum.C1]}: ${
                      selectedDetection.c1Code
                    }`}</li>
                  )}
                  {selectedDetection.c2Code && (
                    <li>{`${SecondaryCodeEnum[SecondaryCodeEnum.C2]}: ${
                      selectedDetection.c2Code
                    }`}</li>
                  )}
                </ul>
              )}
              <ul className="detection-list">
                <li>
                  {translationService.translate(
                    "CreateDamage.modal-confirmation.screenshot-information"
                  )}
                </li>
                {imageUri && (
                  <li>
                    {translationService.translate(
                      "CreateDamage.modal-confirmation.screenshot-timestamp",
                      { timeStamp: screenshotTime }
                    )}
                  </li>
                )}
              </ul>
              {selectedDetectionAlreadyAdded && (
                <div className="damage-already-added">
                  <Svg className="severity-warning" svg={Warning} width="25px" height="25px" />
                  <span>
                    {translationService.translate("CreateDamage.modal-confirmation.damage-added")}
                  </span>
                </div>
              )}
            </div>
            <div>
              <DisplayImage url={imageUri} />
            </div>
          </div>
        ) : (
          <LoadingSpinner spinnerOnly />
        )}
      </div>
    );
  }
);
